import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, checkinApi, ApiError, formatCustomFieldValues, wristbandReceivedLabel, type AdminOrderDto } from '../api';
import QrScanner from '../components/QrScanner';
import { usePageTitle } from '../hooks/usePageTitle';

const TOKEN_KEY = 'jamtick.checkinToken';
const CHECKIN_RESULT_COLOR: Record<'ok' | 'warn' | 'error', string> = { ok: '#9fdcb2', warn: '#d9b36a', error: '#d99a9a' };

type Pending = { payload: string; order: AdminOrderDto };

export default function StaffCheckin() {
  const { eventSlug } = useParams<{ eventSlug?: string }>();

  usePageTitle(eventSlug, 'Check-in');

  const [eventName, setEventName] = useState('');
  useEffect(() => {
    let cancelled = false;
    api.getEvent(eventSlug).then((event) => { if (!cancelled) setEventName(event.name); }).catch(() => {});
    return () => { cancelled = true; };
  }, [eventSlug]);

  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) ?? '');
  const [tokenInput, setTokenInput] = useState('');
  const [loginError, setLoginError] = useState('');
  const [input, setInput] = useState('');
  const [scanning, setScanning] = useState(true);
  const [pending, setPending] = useState<Pending | null>(null);
  /**
   * TICK-062 (R8) — one order is admitted once, for everybody on it. For a group that means the
   * staff member has to state that the whole party is present *before* the button works: a scan
   * taken while half of them are still outside spends the order's only credential, and there is no
   * partial check-in to fall back on.
   */
  const [groupAcknowledged, setGroupAcknowledged] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<{ kind: 'ok' | 'warn' | 'error'; text: string } | null>(null);
  const busy = useRef(false);

  const doLookup = useCallback(
    async (raw: string) => {
      const payload = raw.trim();
      if (!payload || busy.current) return;
      busy.current = true;
      setResult(null);
      try {
        const r = await checkinApi.lookup(token, payload, eventSlug);
        if (r.unverified) {
          setResult({ kind: 'warn', text: `⏳ ${r.code} — ${r.name || '—'} · ยังไม่ยืนยันการชำระเงิน (Unverified) — รอ admin ตรวจสลิปก่อน` });
        } else if (r.alreadyCheckedIn) {
          setResult({ kind: 'error', text: `⚠ ${r.code} — ${wristbandReceivedLabel(r.checkedInAt!)}` });
        } else {
          // Valid, not yet checked in — hold for staff to review before writing anything.
          setPending({ payload, order: r });
          setGroupAcknowledged(false);
        }
        setInput('');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken('');
          setLoginError('Token ไม่ถูกต้อง');
        } else {
          setResult({ kind: 'error', text: err instanceof ApiError ? `✕ ${err.message}` : '✕ ค้นหาไม่สำเร็จ' });
        }
      } finally {
        setTimeout(() => (busy.current = false), 300);
      }
    },
    [token, eventSlug],
  );

  const confirmPassed = useCallback(async () => {
    if (!pending || confirming) return;
    setConfirming(true);
    try {
      const r = await checkinApi.confirm(token, pending.payload, pending.order.entries, eventSlug);
      if (r.alreadyCheckedIn) {
        setResult({ kind: 'error', text: `⚠ ${r.code} — ${wristbandReceivedLabel(r.checkedInAt!)}` });
      } else {
        const linesText = r.lines.map((l) => l.label).join(' · ');
        setResult({ kind: 'ok', text: `✓ ${r.code} — ${r.name || '—'} · ${linesText} · แลกริสแบนด์ได้ ${r.entries} เส้น` });
      }
      setPending(null);
      setGroupAcknowledged(false);
    } catch (err) {
      setResult({ kind: 'error', text: err instanceof ApiError ? `✕ ${err.message}` : '✕ เช็กอินไม่สำเร็จ' });
    } finally {
      setConfirming(false);
      setTimeout(() => (busy.current = false), 300);
    }
  }, [pending, confirming, token, eventSlug]);

  const login = () => {
    const t = tokenInput.trim();
    if (!t) return;
    sessionStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setLoginError('');
  };

  if (!token) {
    return (
      <div className="page admin-login-wrap">
        <h1 style={{ fontSize: 28 }}>{eventName || 'JAMTICK'} — WRISTBAND DESK</h1>
        <p style={{ color: 'var(--muted)', fontSize: 14 }}>ใส่ Check-in token เพื่อเข้าใช้งาน</p>
        <div className="coupon-row" style={{ maxWidth: 420 }}>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && login()}
            placeholder="Check-in token"
          />
          <button className="btn-ghost" onClick={login}>เข้าสู่ระบบ</button>
        </div>
        {loginError && <div className="form-error">{loginError}</div>}
      </div>
    );
  }

  return (
    <div className="page shell" style={{ paddingTop: 32, paddingBottom: 32 }}>
      <div className="admin-order-head">
        <h1 style={{ fontSize: 28, margin: 0 }}>WRISTBAND DESK</h1>
        <button
          className="btn-link"
          onClick={() => {
            sessionStorage.removeItem(TOKEN_KEY);
            setToken('');
          }}
        >
          ออกจากระบบ
        </button>
      </div>

      <div className="card" style={{ maxWidth: 560 }}>
        {pending ? (
          <>
            <div className="card-step">ตรวจสอบก่อนแลกริสแบนด์</div>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 11, padding: 18, marginBottom: 16 }}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{pending.order.code}</div>
              <div style={{ fontSize: 15, marginTop: 6 }}>{pending.order.name || '—'}</div>
              <div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 2 }}>
                {pending.order.phone || '—'}{pending.order.email ? ` · ${pending.order.email}` : ''}
              </div>
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {pending.order.lines.map((l) => {
                  const customText = formatCustomFieldValues(l.customFieldValues);
                  return (
                    <div key={l.ticketId}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 600 }}>
                        <span>{l.label}</span>
                      </div>
                      {customText && (
                        <div style={{ fontSize: 12.5, color: 'var(--dim)', fontFamily: 'var(--mono)', marginTop: 2 }}>{customText}</div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 10, fontFamily: 'var(--mono)' }}>
                รวม {pending.order.qty} ใบ · แลกริสแบนด์ได้ {pending.order.entries} เส้น
              </div>
              {pending.order.wristbands.length > 0 && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 6 }}>ต้องแจก Wristband</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {pending.order.wristbands.map((w) => (
                      <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 600 }}>
                        <span>{w.name} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--dim)' }}>({w.color})</span></span>
                        <span>× {w.qty}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="checkin-whole-order" role="note">
              <strong>เช็กอินทั้งคำสั่งซื้อครั้งเดียว</strong>
              <span>
                QR นี้ใช้ได้ครั้งเดียวสำหรับทั้งคำสั่งซื้อ — ยืนยันแล้วจะใช้ซ้ำไม่ได้
                มอบ Wristband ตามรายการด้านบนให้ผู้ถือ QR ไปทั้งหมด — ไม่ต้องรอให้ทุกคนในออเดอร์มาถึง
              </span>
              {pending.order.entries > 1 && (
                <label className="checkin-ack">
                  <input
                    type="checkbox"
                    checked={groupAcknowledged}
                    onChange={(e) => setGroupAcknowledged(e.target.checked)}
                  />
                  <span>มอบ Wristband สำหรับ {pending.order.entries} คน ให้ผู้ถือ QR ครบแล้ว</span>
                </label>
              )}
            </div>
            <div className="admin-actions">
              <button
                className="btn-primary"
                style={{ padding: '11px 22px' }}
                disabled={confirming || (pending.order.entries > 1 && !groupAcknowledged)}
                onClick={confirmPassed}
              >
                {confirming
                  ? 'กำลังบันทึก…'
                  : `✓ ยืนยันมอบ Wristband สำหรับ ${pending.order.entries} คน`}
              </button>
              <button className="btn-ghost" disabled={confirming} onClick={() => { setPending(null); setGroupAcknowledged(false); }}>ยกเลิก</button>
            </div>
          </>
        ) : (
          <>
            <div className="card-step">สแกน QR เพื่อแลกริสแบนด์ หรือพิมพ์/วางรหัส</div>

            <div style={{ marginBottom: 16 }}>
              {scanning ? (
                <>
                  <QrScanner onDetected={doLookup} />
                  <button className="btn-ghost" style={{ marginTop: 12 }} onClick={() => setScanning(false)}>
                    ปิดกล้อง
                  </button>
                </>
              ) : (
                <button className="btn-primary" style={{ padding: '12px 22px' }} onClick={() => { setResult(null); setScanning(true); }}>
                  📷 เปิดกล้องสแกน QR
                </button>
              )}
            </div>

            <div className="coupon-row" style={{ marginTop: 0 }}>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doLookup(input)}
                placeholder="QR payload / order code"
              />
              <button className="btn-primary" style={{ padding: '10px 20px' }} onClick={() => doLookup(input)}>
                ค้นหา
              </button>
            </div>

            {result && (
              <div className="checkin-result" style={{ fontSize: 15, color: CHECKIN_RESULT_COLOR[result.kind] }}>
                {result.text}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
